import { Injectable, Injector } from '@angular/core';
import { MediaBankFileModel } from './media-bank-file.model';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { MediaBankService } from './media-bank.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import {
    ItemsListElementType,
    ItemsListHeader,
    ItemsListHeaderProperties,
} from '@fi-app/components/items-list/items-list-header';
import { FiMediaBankFileModel } from '@fi-shared/models';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { FiSiteDesignAccessItemCellComponent } from '@fi-app/fi-site-design/fi-site-design-access-item-cell/fi-site-design-access-item-cell.component';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';

interface IMediaBankListOptions {
    isFavoriteIconShown?: boolean;
    isLocalSort?: boolean;
}

@Injectable()
export class MediaBankListDataService {
    constructor(private injector: Injector) {}

    getInstance(opts?: IMediaBankListOptions) {
        return new MediaBankListData(this.injector, opts);
    }
}

export class MediaBankListData extends ListData<MediaBankFileModel> {
    private notificationHelper: FiNotificationHelperService;
    constructor(injector: Injector, opts?: IMediaBankListOptions) {
        if (!opts) {
            opts = {};
        }

        const mediaBankService = injector.get(MediaBankService);
        const itemsDeleteHelperService = injector.get(FiItemsDeleteHelperService);
        const mediaBankHelperService = injector.get(MediaBankHelperService);
        const fiFormatDateService = injector.get(FiFormatDateService);

        super(injector, opts.isLocalSort, 'PublicationStartDate', true);

        this.notificationHelper = injector.get(FiNotificationHelperService);

        this.IconClass = (mediaBankFile) => {
            return mediaBankService.GetMediaBankFileIcon(mediaBankFile.FileType);
        };
        this.isV2SearchByContent = true;

        this.ShowFavoriteIcon = opts.isFavoriteIconShown;
        this.ObjectType = ObjectTypes.MediaBank;

        this.DeletePermanentlyTextId = 'mediabank.delete_permanently_confirm';

        this.Header.push(
            new ItemsListHeader(
                '',
                '',
                new ItemsListHeaderProperties(
                    'CcmListItem-Icon CcmListItem-NoBorder',
                    true,
                    ItemsListElementType.ImgIconA,
                    {
                        Sref: FiStateNames.MediaBank.View,
                        SrefParams: (data) => ({ Id: data.Id }),
                        ImgSrc: 'Image',
                        ImgSrcLarge: 'ImageLarge',
                    },
                ),
            ),
        );

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.name'),
                'Name',
                new ItemsListHeaderProperties('CcmListItem-MediaBank CcmListItem-Name', true, ItemsListElementType.A, {
                    Sref: FiStateNames.MediaBank.View,
                    SrefParams: (data) => ({ Id: data.Id }),
                    secondaryValue: (data) => {
                        return fiFormatDateService.formatDate(data.ChangedOn, 'date') + ' ' + data.ChangedByUserName;
                    },
                }),
            ),
        );

        if (this.userDataService.showSiteDesignAccessForItems()) {
            this.Header.push(
                new ItemsListHeader(
                    this.appTextService.get('site_design.brands'),
                    'SiteDesignIds',
                    new ItemsListHeaderProperties('CcmListItem-Brand', false, FiSiteDesignAccessItemCellComponent),
                ),
            );
        }

        this.addSpanPlainHeader(
            this.appTextService.get('common.format'),
            'Extension',
            'CcmList-Column-Extra-Narrow',
            true,
            (data: MediaBankFileModel) => {
                if (data.Extension) return data.Extension.substring(1).toUpperCase();
            },
        );

        this.addSpanPlainHeader(
            this.appTextService.get('common.size'),
            'Size',
            'CcmList-Column-Extra-Narrow',
            true,
            (data: MediaBankFileModel, prop) => {
                return mediaBankHelperService.getFileSizeText(data.Size);
            },
        );

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.tags'),
                'Tags',
                new ItemsListHeaderProperties('', false, ItemsListElementType.Tags),
                'Name',
            ),
        );

        this.addStartDateHeader(true);

        this.addChangedOnHeader();

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.remark'),
                'IsPublished',
                new ItemsListHeaderProperties('', true, ItemsListElementType.SpanPlain, {
                    getMainValueClass: (data: MediaBankFileModel) => {
                        if (!data) {
                            return '';
                        }
                        if (data.IsDeleted) {
                            return 'CcmListItemRed';
                        }
                        if (data.IsPublished) {
                            return 'CcmListItemGreen';
                        }
                        return 'CcmListItemOrange';
                    },
                }),
                undefined,
                undefined,
                (data: MediaBankFileModel) => {
                    if (data.IsDeleted) return this.appTextService.get('document.deleted');
                    if (data.IsPublished) return '';
                    return this.appTextService.get('common.not_shared');
                },
            ),
        );

        this.deleteConfirm = (
            deleteConfirmMessage: string,
            selectedIds: number[],
            selectedItems: MediaBankFileModel[],
        ) => {
            return itemsDeleteHelperService.mediaBankDeleteConfirm(
                selectedItems,
                this.appTextService.get(this.DeletePermanentlyTextId),
            );
        };

        this.deleteFunc = (mediaBankFileIdsToDelete) => mediaBankService.DeleteMediaBankFiles(mediaBankFileIdsToDelete);

        this.restoreFunc = (mediaBankFilesToRestore) => {
            const mediaBankFileIdsToRestore = mediaBankFilesToRestore.map((mediaBankFile) => mediaBankFile.Id);
            return mediaBankService.RestoreMediaBankFiles(mediaBankFileIdsToRestore);
        };

        this.getRowClass = (file: FiMediaBankFileModel) => {
            const result: string[] = [];
            if (this.notificationHelper.isNotSeen(file)) result.push('not-seen');
            if (this.notificationHelper.isNotVisited(file)) result.push('bold');
            return result.join(' ');
        };
    }

    protected getKeyForListSort(): FiItemsListType {
        return FiItemsListType.MediaBank;
    }
}
